import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Services.css';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Services = ({ initialTab = null, variant = 'home' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const sectionRef = useScrollAnimation();

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const serviceTabs = [
    { id: 'taxation', label: 'Taxation', icon: '📋' },
    { id: 'bookkeeping', label: 'Bookkeeping', icon: '📊' },
    { id: 'payroll', label: 'Payroll', icon: '💰' },
    { id: 'financial-planning-consultation', label: 'Financial Planning & Consultation', icon: '🎯' },
    { id: 'incorporation', label: 'Business Incorporation', icon: '🏢' },
    { id: 'gst-hst', label: 'GST/HST Filing', icon: '🏠' },
    { id: 'wsib-filing', label: 'WSIB Filing', icon: '💼' }
  ];

  // Initialize active tab on mount
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && serviceTabs.some(t => t.id === hash)) {
      setActiveTab(hash);
    }
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If element not found (e.g., on service detail page), navigate to home page
      window.location.href = '/';
      setTimeout(() => {
        const homeElement = document.getElementById(sectionId);
        if (homeElement) {
          homeElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <section id="services" className="services section fade-in-up" ref={sectionRef}>
      <div className="container">
        {variant === 'home' && (
          <div className="section-header">
            <h2 className="section-title">Our Core Services</h2>
            <p className="section-subtitle">Choose a service below to learn more about our comprehensive accounting solutions</p>
          </div>
        )}

        {variant === 'home' && (
          <div className="service-cards-grid">
            {serviceTabs.map(tab => (
              <Link key={tab.id} to={`/services/${tab.id}`} className="service-card-image" onClick={() => setActiveTab(tab.id)}>
                <div className="card-bg" data-service={tab.id}></div>
                <div className="card-overlay"></div>
                <div className="card-content">
                  <div className="card-title">
                    <span className="card-icon">{tab.icon}</span>
                    {tab.label}
                  </div>
                  <div className="card-cta">Read more →</div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Service Content - Only show when a tab is active */}
        {activeTab && (
          <div className="service-content">

          {/* Taxation */}
          {activeTab === 'taxation' && (
            <div id="taxation" className="service-detail">
              <div className="service-header service-header-left">
                <h2 className="main-service-title">Corporate and Personal Tax Filing</h2>
                <h2>Personal Tax Filing</h2>
                <p>Expert preparation services eliminating stress and confusion from Canadian tax filing</p>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Income Sources We Handle</h4>
                  <ul>
                    <li>Employment Income (T4, T4A)</li>
                    <li>Pension and Annuities (T4A, T4AOAS, T4AP, T4RIF, T4RSP)</li>
                    <li>Investment Income (T5, T3, T600, T5008, T5013, T5013A)</li>
                    <li>Foreign Assets and Rental Income</li>
                    <li>RRSP Income (T4RSP)</li>
                    <li>Capital Gains and Losses Schedules</li>
                    <li>Self-Employment Income</li>
                    <li>Debt Forgiveness</li>
                    <li>Other Income (T4E, T4PS, T5007, RC62, RC210)</li>
                  </ul>
                </div>

                <div className="service-card">
                  <h4>Deductions We Maximize</h4>
                  <ul>
                    <li>Employment Expenses (Form 2200)</li>
                    <li>Investment deductions (T101, T102, T5003, T5004, T1-CP)</li>
                    <li>Rental Property Expenses</li>
                    <li>RRSP Contributions (up to March 1 following year)</li>
                    <li>Professional Dues and Membership Fees</li>
                    <li>Moving Expenses, Child Care Expenses</li>
                    <li>Alimony and Maintenance (T1157)</li>
                    <li>Disability Support Expenses</li>
                    <li>Business Investment Losses (ABIL)</li>
                    <li>Legal Fees</li>
                  </ul>
                </div>

                <div className="service-card">
                  <h4>Tax Credits Optimization</h4>
                  <ul>
                    <li>Spousal Exemption, Dependents</li>
                    <li>Disability Amounts (T2201)</li>
                    <li>Adoption Expenses</li>
                    <li>Education amounts (T2202/T2202A)</li>
                    <li>Student Loan Interest</li>
                    <li>Medical Expenses, Caregiver Amounts</li>
                    <li>Charitable Donations</li>
                    <li>Political Contributions</li>
                    <li>Transit Pass, Rent/Property Tax Credits</li>
                    <li>Child Fitness and Arts Credits</li>
                  </ul>
                </div>

                <div className="service-card">
                  <h4>Ontario-Specific Tax Credits</h4>
                  <ul>
                    <li>Healthy Homes Tax Credit</li>
                    <li>Ontario Focused Flow Through Share Tax Credit</li>
                    <li>Children's Ontario Tax Credit</li>
                    <li>Political Contribution Tax Credit</li>
                  </ul>
                </div>
              </div>

              <div className="service-card" id="tax-deadlines">
                <h4>Tax Filing Deadlines</h4>
                <p>Tax returns should be filed on time. Profit Pioneers Accounting & Bookkeeping Inc. ensures that your personal tax return is submitted on time. The reporting date for employed Canadians is on April 30th of each year. Self-employed individuals have until June 15th to file their personal income tax forms, but any outstanding taxes must be paid by April 30th to avoid interest and penalties.</p>
              </div>

              {/* Corporate Tax Filing Section */}
              <div className="corporate-tax-section">
                <h3>Corporate Tax Filing</h3>
                <p>As a Canadian business owner, understanding your corporate tax responsibilities is crucial for staying on track with the <em>Canada Revenue Agency (CRA)</em>. Every resident corporation—whether it's active, inactive, non-profit, or tax-exempt—is required to file a corporate income tax (T2) return every year. This also applies even if you don't owe any taxes! Here's who needs to file:</p>
                
                <ul className="corporate-tax-list">
                  <li><em>Non-profit organizations</em></li>
                  <li><em>Tax-exempt corporations</em></li>
                  <li><em>Inactive corporations</em></li>
                  <li><em>Note</em>: Crown corporations and registered charities are exempt from this requirement.</li>
                </ul>

                <p>At <em>Profit Pioneers Accounting & Bookkeeping Inc.</em>, we make the corporate tax filing process as simple and stress-free as possible. Our team of experts ensures your tax return is filed on time, accurately, and in full compliance with CRA regulations—so you can focus on running your business.</p>

                <div className="tax-deadlines-section">
                  <h4><em>Key Tax Filing Deadlines You Should Know</em></h4>
                  
                  <div className="deadline-item">
                    <h5><em>Filing Deadline:</em></h5>
                    <p>Corporations have up to six months after the end of their fiscal year to file their T2 tax return. Here's the breakdown:</p>
                    <ul>
                      <li>If your fiscal year ends on the last day of a month, your tax return is due by the last day of the sixth month after your fiscal year ends.</li>
                      <li>If your fiscal year ends on a date other than the last day of the month, your tax return is due on the corresponding day of the sixth month following your fiscal year-end.</li>
                    </ul>
                  </div>

                  <div className="deadline-item">
                    <h5><em>Payment Deadline:</em></h5>
                    <p>Any taxes owed must be paid within three months (90 days) after the end of your fiscal year. If payment is made after this deadline, interest and penalties will apply. So, make sure you're on top of both filing and payment deadlines!</p>
                  </div>
                </div>

                <p>At <em>Profit Pioneers Accounting & Bookkeeping Inc.</em>, we're here to make sure you meet these deadlines with ease. Our goal is to provide affordable, hassle-free tax services that keep your business compliant, stress-free, and on the path to success.</p>
              </div>

            </div>
          )}

          {/* GST/HST Filing */}
          {activeTab === 'gst-hst' && (
            <div id="gst-hst" className="service-detail">
              <div className="service-header service-header-left">
                <h3>GST/HST Filing</h3>
                <p>Managing GST/HST doesn't have to be complicated. Along with our specialized housing rebates, we provide a full range of services to help you save money, stay compliant, and reduce stress.</p>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Specialized GST/HST Services</h4>
                  <ul>
                    <li><strong>New Housing Rebate:</strong> Maximum 36% of GST paid, up to $6,300 federal rebate</li>
                    <li><strong>Provincial Housing Rebate:</strong> Ontario-specific HST rebate for properties under $450,000</li>
                    <li><strong>Eligibility Assessment:</strong> Determining qualification for various rebate programs</li>
                    <li><strong>Compliance Management:</strong> Ensuring proper GST/HST filing and maximizing savings</li>
                  </ul>
                </div>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Rebate Conditions Managed</h4>
                  <ul>
                    <li><strong>Principal Residence Requirements:</strong> Ensuring your property meets principal residence criteria for rebate eligibility</li>
                    <li><strong>Property Verification:</strong> New or substantially renovated property verification and documentation</li>
                    <li><strong>Purchase Price Thresholds:</strong> Managing documentation for properties within rebate thresholds</li>
                  </ul>
                </div>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Additional GST/HST Support and Advisory Services</h4>
                  <ul>
                    <li><strong>Smart Tax Strategies:</strong> We identify opportunities to optimize your GST/HST filings and maximize your eligible benefits.</li>
                    <li><strong>Accurate & Timely Filing:</strong> Never worry about penalties or late submissions—your returns are prepared and filed on time, every time.</li>
                    <li><strong>Maximize Refunds & Rebates:</strong> From federal to provincial programs, we make sure you claim everything you're entitled to.</li>
                    <li><strong>Stay Compliant, Stress-Free:</strong> We keep your documentation and filings up to date, navigating evolving rules so you don't have to.</li>
                    <li><strong>CRA Communication Made Easy:</strong> Any notices or queries from the Canada Revenue Agency? We handle them efficiently, ensuring clear and correct responses.</li>
                  </ul>
                </div>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Why Choose Us?</h4>
                  <ul>
                    <li><strong>Trusted Expertise:</strong> Years of experience with GST, HST, and rebate programs mean you're in capable hands.</li>
                    <li><strong>Tailored Solutions:</strong> Services designed specifically for your property or business to unlock maximum benefits.</li>
                    <li><strong>Peace of Mind:</strong> Focus on your priorities while we take care of the GST/HST complexities.</li>
                  </ul>
                </div>
              </div>

              <div className="service-conclusion">
                <p>With our combination of specialized rebate knowledge and comprehensive compliance support, you'll save more, reduce risk, and stay fully aligned with all GST/HST requirements.</p>
              </div>
            </div>
          )}

          {/* Financial Planning & Consultation */}
          {activeTab === 'financial-planning-consultation' && (
            <div id="financial-planning-consultation" className="service-detail">
              <div className="service-header service-header-left">
                <h3>Financial Planning & Consultation</h3>
                <p>Strong financial planning is the foundation of a resilient business. Our team helps you look beyond day-to-day accounting and build strategies that protect your assets, lower taxes, and support long-term growth.</p>
              </div>
              
              <div className="service-grid">
                <div className="service-card">
                  <h4>Strategic Services</h4>
                  <ul>
                    <li><strong>Comprehensive Financial Statements:</strong> Prepare and review balance sheets, income statements, and cash-flow reports to understand your company's financial position and performance.</li>
                    <li><strong>Business Feasibility Reviews:</strong> Evaluate new ideas or expansion plans with detailed feasibility studies that measure risk, market potential, and financial viability.</li>
                    <li><strong>Income-Splitting Guidance:</strong> Create family income-splitting strategies to legally reduce overall tax obligations.</li>
                    <li><strong>Tax-Efficiency Planning:</strong> Identify opportunities to minimize taxes through careful structuring and proactive advice.</li>
                    <li><strong>Asset Protection Planning:</strong> Develop safeguards to protect both personal and corporate assets from unexpected liabilities.</li>
                  </ul>
                </div>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Why Work With Us</h4>
                  <ul>
                    <li><strong>Strategic Insights:</strong> Every business is unique, so our plans are built around your specific goals and challenges.</li>
                    <li><strong>Forward-Looking Advice:</strong> We help you anticipate future needs, from succession planning to capital requirements, so you stay a step ahead.</li>
                    <li><strong>Full-Scope Support:</strong> From early-stage strategy sessions to ongoing financial guidance, we partner with you through each stage of growth.</li>
                  </ul>
                </div>
              </div>

              <div className="service-conclusion">
                <p>Whether you're launching a new venture or strengthening an established company, our financial planning and consultation services provide a clear roadmap and the confidence to make informed decisions.</p>
              </div>
            </div>
          )}

          {/* Business Incorporation */}
          {activeTab === 'incorporation' && (
            <div id="incorporation" className="service-detail">
              <div className="service-header service-header-left">
                <h3>Business Incorporation Services</h3>
                <p>Starting a business is exciting, but the legal steps can feel overwhelming. Our incorporation services make the process smooth and worry-free while ensuring your company is built on a strong legal foundation.</p>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Complete Incorporation Support</h4>
                  <p>We handle every stage of creating a corporation anywhere in Canada, from the first consultation to final registration. Our team assists with federal or provincial incorporation, master business licence registration, and not-for-profit company setup. Whether you need a simple owner-managed corporation or a more complex structure, we prepare and file all the necessary documentation accurately and on time.</p>
                </div>

                <div className="service-card">
                  <h4>Ongoing Corporate Maintenance</h4>
                  <p>Business needs change, and we're here when they do. We manage director additions or removals, corporate amendments, and voluntary dissolutions, making sure each change complies with current regulations and is properly recorded.</p>
                </div>

                <div className="service-card">
                  <h4>Why Incorporate With Us</h4>
                  <ul>
                    <li><strong>Liability Protection:</strong> Separate your personal assets from business obligations and reduce risk.</li>
                    <li><strong>Tax Efficiency:</strong> Take advantage of lower corporate tax rates compared with individual income tax and explore opportunities for income splitting within the family.</li>
                    <li><strong>Credibility & Funding:</strong> A registered corporation builds trust with clients, investors, and lenders, helping you access capital more easily.</li>
                    <li><strong>Long-Term Stability:</strong> Your corporation continues to exist even if ownership or management changes.</li>
                  </ul>
                </div>

                <div className="service-card">
                  <h4>Our Process</h4>
                  <ul>
                    <li><strong>Consultation & Guidance</strong> – We discuss your goals, explain the differences between federal and provincial incorporation, and recommend the best structure.</li>
                    <li><strong>Name Search & Registration</strong> – Thorough corporate name checks ensure your chosen name is unique and compliant.</li>
                    <li><strong>Filing & Approval</strong> – We prepare and submit all forms and follow through until your incorporation certificate is issued.</li>
                    <li><strong>Support After Setup</strong> – From annual filings to minute-book maintenance, we help keep your corporation in good standing.</li>
                  </ul>
                </div>
              </div>

              <div className="service-card" id="incorporation-cta">
                <p><strong>Launch your business with the confidence that every legal detail is covered.</strong> Our experienced professionals combine practical advice with reliable service so you can focus on growing your company while we take care of the paperwork.</p>
              </div>
            </div>
          )}

          {/* Bookkeeping Services */}
          {activeTab === 'bookkeeping' && (
            <div id="bookkeeping" className="service-detail">
              <div className="service-header service-header-left">
                <h3>Bookkeeping Services</h3>
                <p>Accurate, well-organized books are the backbone of every successful business. We deliver end-to-end bookkeeping solutions designed to fit businesses of any size—whether you're a start-up building your foundation or an established company managing complex finances.</p>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Fundamental Services</h4>
                  <ul>
                    <li><strong>Chart of Accounts:</strong> Creation and maintenance of a complete list of general ledger accounts, classified into Assets, Liabilities, Owner's Equity, Revenue, and Expenses.</li>
                    <li><strong>Bank Reconciliation:</strong> Careful matching of bank statements with cash receipts and payments, including preparation of receivable and supplier statements for error-free records.</li>
                    <li><strong>Transaction Recording:</strong> Daily recording of transactions using a precise double-entry system with proper debit and credit entries.</li>
                    <li><strong>Balancing of Books:</strong> Quarterly balancing where all debits and credits are tallied and a trial balance is prepared.</li>
                  </ul>
                </div>

                <div className="service-card">
                  <h4>Financial Statement Preparation</h4>
                  <ul>
                    <li>Balance Sheet</li>
                    <li>Income Statement</li>
                    <li>Cash Flow Statement</li>
                  </ul>
                </div>

                <div className="service-card">
                  <h4>Why Partner with Us for Bookkeeping</h4>
                  <ul>
                    <li><strong>Customized Approach</strong> – We tailor our methods to your unique business model and growth plans, ensuring your books reflect the reality of your operations.</li>
                    <li><strong>Free Up Your Time</strong> – Let us handle the day-to-day recording and reconciliations so you can devote energy to strategy and expansion.</li>
                    <li><strong>Smart Technology</strong> – With the latest accounting platforms, you'll have quick access to accurate financial data and easy-to-read reports whenever you need them.</li>
                    <li><strong>Meticulous Record-Keeping</strong> – Every transaction is double-checked, helping you stay compliant and ready for audits without last-minute stress.</li>
                  </ul>
                </div>

                <div className="service-card">
                  <h4>Key Advantages</h4>
                  <ul>
                    <li>Actionable insights that support confident financial decisions</li>
                    <li>Stronger cash-flow control and improved forecasting</li>
                    <li>Well-organized records that make tax season straightforward</li>
                    <li>Peace of mind knowing seasoned professionals manage every detail</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* WSIB Filing */}
          {activeTab === 'wsib-filing' && (
            <div id="wsib-filing" className="service-detail">
              <div className="service-header service-header-left">
                <h3>WSIB Filing</h3>
                <p>WSIB regulations can be complicated, but we make the process straightforward. We assist with determining the right classification, reporting schedule, and coverage requirements, helping you stay compliant while minimizing risk. We also offer advice on exemptions and alternative coverage options, ensuring your business only pays for what it truly needs.</p>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Workplace Safety Insurance Services</h4>
                  <ul>
                    <li><strong>WSIB Registration:</strong> Complete support for registering your business with the Workplace Safety and Insurance Board, ensuring all details are accurate and compliant.</li>
                    <li><strong>Ongoing Compliance:</strong> Continuous monitoring and guidance to ensure your business stays aligned with WSIB regulations.</li>
                    <li><strong>Premium Management:</strong> Accurate calculation and timely submission of WSIB premiums to avoid errors or penalties.</li>
                    <li><strong>Claim Support:</strong> Assistance with workplace injury claims, documentation, and follow-ups to ensure proper processing.</li>
                  </ul>
                </div>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Why Choose Us?</h4>
                  <ul>
                    <li><strong>Proven Expertise:</strong> Extensive knowledge of WSIB rules and compliance for a variety of industries.</li>
                    <li><strong>Reliable Filings:</strong> Accurate registration, reporting, and premium submissions delivered on time.</li>
                    <li><strong>Reduced Risk:</strong> Proactive support to help you avoid fines, penalties, and other compliance issues.</li>
                    <li><strong>Business-Centric Approach:</strong> Solutions tailored to your company's structure, industry, and operations.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Payroll Services */}
          {activeTab === 'payroll' && (
            <div id="payroll" className="service-detail">
              <div className="service-header service-header-left">
                <h3>Payroll Services</h3>
                <p>Payroll is an essential part of running a business, and we make it simple and hassle-free. Our services handle everything from calculating employee salaries, managing benefits and deductions, to ensuring WSIB registration and compliance. We also take care of all tax calculations, filings, and reporting, so your business remains fully compliant while you concentrate on growth and day-to-day operations.</p>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Comprehensive Payroll Management</h4>
                  <ul>
                    <li><strong>Payroll Services for Small Businesses:</strong> Complete payroll processing and management tailored to your business needs.</li>
                    <li><strong>WSIB Registration:</strong> Workplace Safety and Insurance Board registration and ongoing compliance support.</li>
                    <li><strong>Regular Payroll Calculations:</strong> Accurate processing of employee wages, deductions, benefits, and remittances.</li>
                    <li><strong>Compliance Management:</strong> Ensuring full adherence to all applicable employment laws and regulations.</li>
                  </ul>
                </div>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Payroll Made Easy</h4>
                  <p>We handle every aspect of payroll so your business can run smoothly. From calculating wages and managing deductions to processing benefits and WSIB compliance, our solutions ensure accuracy and reliability while keeping you fully compliant.</p>
                  <ul>
                    <li><strong>Employee Records:</strong> Keep all payroll information organized and secure.</li>
                    <li><strong>Taxes & Remittances:</strong> Accurate calculation and on-time submission of all payroll taxes.</li>
                    <li><strong>Benefits & Allowances:</strong> Manage bonuses, deductions, and employee benefits seamlessly.</li>
                    <li><strong>Payroll Reports:</strong> Detailed insights for smarter business decisions.</li>
                  </ul>
                </div>
              </div>

              <div className="service-grid">
                <div className="service-card">
                  <h4>Why Choose Us?</h4>
                  <p>Our expertise ensures stress-free, accurate payroll processing Customized to your business needs, letting you focus on growth while we handle the details.</p>
                </div>
              </div>
            </div>
          )}
          </div>
        )}


        {/* CTA Section */}
        <div className="services-cta">
          <h3>Ready to Get Started?</h3>
          <p>Contact us today for a free consultation and let us handle your accounting needs</p>
          <div className="cta-buttons">
            <button 
              onClick={() => scrollToSection('contact')} 
              className="btn btn-primary"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
